import {
  Euler,
  type Group,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  type Scene,
  Vector3,
} from 'three';
import { buildLane } from '../world/laneBuilder';
import { loadMaterials, type MaterialSet } from '../world/materials';
import { generateBoxUVs } from '../world/ramps';
import type { BuiltLevel } from '../world/TestLevel';
import { laneSpecFor, SHAPES, type ShapeId, type ShapeRole } from './shapes';

/** One placed shape: which shape, its (optional) tuned params, and its transform. */
export interface Placement {
  shapeId: ShapeId;
  /** Overrides for the shape's default dimensions. Omitted axes use defaults. */
  params?: Record<string, number>;
  position: [number, number, number];
  /** Euler angles in radians, applied in YXZ order (matches the game's ramps). */
  rotation: [number, number, number];
  scale?: [number, number, number];
  /** Overrides the shape's default play-time role. */
  role?: ShapeRole;
}

/**
 * The editor's save file: a list of shape placements plus the spawn/kill data
 * the runtime needs. This stores *intent* (shape + transform), so it round-trips
 * losslessly and re-instantiates through the same ramp factories the game uses —
 * unlike a baked .glb, which is the shipping format, not the editable one.
 */
export interface PlacementMapSpec {
  version: 1;
  id: string;
  name: string;
  spawn: [number, number, number];
  spawnYaw: number;
  killY?: number;
  placements: Placement[];
}

export function emptySpec(id = 'untitled', name = 'Untitled'): PlacementMapSpec {
  return { version: 1, id, name, spawn: [0, 200, 0], spawnYaw: 0, placements: [] };
}

const RANDOM_KILL_Y = 40;
/** Upper bound on generated features (safety clamp; the editor + big maps pass counts). */
const MAX_GENERATED_FEATURES = 800;
/** Lowest a piece's base may sit — keeps the whole course above the lava. */
const COURSE_FLOOR = 320;
const COURSE_MATERIAL = new MeshStandardMaterial();

/** Extra rotation.x (degrees) that tilts a straight piece downhill. */
const STRAIGHT_PITCH_DEG: Partial<Record<ShapeId, number>> = {
  surfPrism: 10,
  ramp: 12,
  wallRamp: 9,
  halfpipe: 8,
  quarterpipe: 8,
  transferGap: 9,
};

/**
 * Ramp types the generated course strings together. Restricted to the **prism
 * family** — straight surf prisms and banked lanes (curves/sbend/corkscrew, which
 * `buildLane` builds from the same `makeSurfPrism` cross-section). Shapes with a
 * different cross-section (U-troughs, walls, cones, jump gaps) and the wedge are
 * deliberately excluded: a rider can't slide from one cross-section onto another
 * without rubbing the seam, so keeping a single shared profile is what makes the
 * joins smooth (owner directive: "as smooth as possible").
 */
const COURSE_POOL: ShapeId[] = [
  'surfPrism',
  'curveL90',
  'curveR90',
  'curveL45',
  'curveR45',
  'sbend',
  'corkscrew',
];

/** Unified generated-course ride profile, so every piece shares one cross-section. */
const GEN_WIDTH = 896;
const GEN_FACE_ANGLE = 56;
const GEN_DECLINE = 10;

/**
 * Per-shape geometry overrides that pin every generated piece to the same width,
 * face angle and decline — so chained pieces share an identical surf cross-section
 * and meet flush (coplanar slopes, no seam step). `caps:0` drops the end walls.
 */
function genOverrides(shapeId: ShapeId): Record<string, number> {
  if (shapeId === 'surfPrism') {
    return { width: GEN_WIDTH, angleDeg: GEN_FACE_ANGLE, length: 3200, caps: 0 };
  }
  return { width: GEN_WIDTH, faceAngle: GEN_FACE_ANGLE, decline: GEN_DECLINE, caps: 0 };
}

function straightLength(shapeId: ShapeId, d: Record<string, number>): number {
  if (shapeId === 'transferGap') {
    return (d.segLength ?? 2200) * 2 + (d.gap ?? 640);
  }
  return d.length ?? 4096;
}

/**
 * Where a feature, entered at its local origin facing +Z, hands the rider off:
 * the exit point in its local frame, the heading change through it, and the
 * pitch to tilt it downhill (0 for curves, whose decline is baked in).
 * `overrides` tune the feature's dimensions so the exit stays accurate.
 */
export function featureExit(
  shapeId: ShapeId,
  overrides?: Record<string, number>,
): { local: Vector3; turnDeg: number; pitch: number } {
  const d = { ...SHAPES[shapeId].defaults, ...overrides };
  const spec = laneSpecFor(shapeId, d);
  if (spec) {
    const { cursor, headingDeg } = buildLane(spec, new Vector3(), 0, COURSE_MATERIAL);
    return { local: cursor, turnDeg: headingDeg, pitch: 0 };
  }
  if (shapeId === 'spire') {
    const r = d.baseRadius ?? 640;
    const h = d.height ?? 1200;
    return { local: new Vector3(r * 1.7, -h * 0.5, r * 0.9), turnDeg: 75, pitch: 0 };
  }
  if (shapeId === 'platform') {
    return { local: new Vector3(0, 0, d.depth ?? 768), turnDeg: 0, pitch: 0 };
  }
  const pitch = ((STRAIGHT_PITCH_DEG[shapeId] ?? 9) * Math.PI) / 180;
  return { local: new Vector3(0, 0, straightLength(shapeId, d)), turnDeg: 0, pitch };
}

function shuffle<T>(items: T[], rng: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i];
    const b = out[j];
    if (a !== undefined && b !== undefined) {
      out[i] = b;
      out[j] = a;
    }
  }
  return out;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const arr = (v: Vector3): [number, number, number] => [
  Math.round(v.x),
  Math.round(v.y),
  Math.round(v.z),
];

/**
 * Appends one branch to `placements`: a steep, long drop-in ramp (so you arrive
 * with real speed), then `featureCount - 1` chained ramp features, then a
 * landing pad. Each feature's true exit becomes the next one's entry.
 */
function buildBranch(
  placements: Placement[],
  start: Vector3,
  headingDeg: number,
  featureCount: number,
  rng: () => number,
): void {
  let heading = headingDeg;
  const cursor = start.clone();
  const place = (
    shapeId: ShapeId,
    params: Record<string, number> | undefined,
    pitch: number,
    exitLocal: Vector3,
    turnDeg: number,
  ): void => {
    placements.push({
      shapeId,
      params,
      position: arr(cursor),
      rotation: [pitch, toRad(heading), 0],
    });
    cursor.copy(
      exitLocal
        .clone()
        .applyEuler(new Euler(pitch, toRad(heading), 0, 'YXZ'))
        .add(cursor),
    );
    heading += turnDeg;
  };

  // Steep, long in-run: turns the high drop-in into entry speed. Same cross-section
  // as the rest of the branch (just steeper + longer) so the first join is flush.
  place(
    'surfPrism',
    { length: 6000, width: GEN_WIDTH, angleDeg: GEN_FACE_ANGLE, caps: 0 },
    toRad(22),
    new Vector3(0, 0, 6000),
    0,
  );

  const lead = shuffle(COURSE_POOL, rng);
  for (let i = 0; i < featureCount - 1; i++) {
    const shapeId: ShapeId =
      (i < lead.length ? lead[i] : COURSE_POOL[Math.floor(rng() * COURSE_POOL.length)]) ??
      'surfPrism';
    // Same overrides drive both the exit math and the placed geometry, so the
    // next piece lands exactly on this one's exit edge with a matching profile.
    const overrides = genOverrides(shapeId);
    const { local, turnDeg, pitch } = featureExit(shapeId, overrides);
    place(shapeId, overrides, pitch, local, turnDeg);
  }

  // Landing pad at the end of the branch.
  placements.push({
    shapeId: 'platform',
    position: arr(cursor),
    rotation: [0, toRad(heading), 0],
    params: { width: 1100, height: 64, depth: 1100 },
  });
}

/**
 * Generates a fully surfable course from a tall central spawn pad. Each branch
 * opens with a steep drop-in ramp (high start → real entry speed) then chains
 * ramp features downhill, using every ramp type. Past 200 features the course
 * fans out into several branches radiating in different directions from the pad.
 * The whole thing is lifted to float above the lava. A start to refine in-editor.
 */
export function randomSpec(
  featureCount: number,
  rng: () => number = Math.random,
): PlacementMapSpec {
  const count = Math.max(1, Math.min(MAX_GENERATED_FEATURES, Math.floor(featureCount)));
  const placements: Placement[] = [];
  const baseHeading = rng() * 360;

  // Tall central spawn pad — walk off any edge onto a branch's drop-in ramp.
  const pad = new Vector3(0, 6000, 0);
  placements.push({
    shapeId: 'platform',
    position: arr(pad),
    rotation: [0, toRad(baseHeading), 0],
    params: { width: 1400, height: 96, depth: 1400 },
  });
  const spawn: [number, number, number] = [pad.x, pad.y + 190, pad.z];
  const spawnYaw = toRad(baseHeading) + Math.PI;

  // Past 200 features, fan out into several branches in different directions.
  const branches = count > 200 ? Math.min(8, Math.max(3, Math.round(count / 80))) : 1;
  const per = Math.max(1, Math.floor(count / branches));
  for (let b = 0; b < branches; b++) {
    const heading =
      branches > 1 ? baseHeading + (b * 360) / branches + (rng() - 0.5) * 16 : baseHeading;
    const branchStart = pad.clone();
    branchStart.x += Math.sin(toRad(heading)) * 460;
    branchStart.z += Math.cos(toRad(heading)) * 460;
    branchStart.y -= 260; // just under the pad lip; the drop-in does the descending
    buildBranch(placements, branchStart, heading, per, rng);
  }

  // Lift the whole course so its lowest piece floats above the lava.
  let minY = Number.POSITIVE_INFINITY;
  for (const p of placements) {
    minY = Math.min(minY, p.position[1]);
  }
  if (minY < COURSE_FLOOR) {
    const dy = COURSE_FLOOR - minY;
    for (const p of placements) {
      p.position[1] += dy;
    }
    spawn[1] += dy;
  }

  return {
    version: 1,
    id: 'random',
    name: 'Random surf course',
    spawn,
    spawnYaw,
    killY: RANDOM_KILL_Y,
    placements,
  };
}

/** One feature in a hand-authored course sequence (default dims unless tuned). */
export interface CourseFeature {
  shapeId: ShapeId;
  params?: Record<string, number>;
  /** Override the default downhill pitch (degrees); ignored by curved lanes. */
  pitchDeg?: number;
}

export interface CourseSequence {
  placements: Placement[];
  /** World entry point (base-front origin) of each feature, in order. */
  entries: Vector3[];
  /** Heading (degrees) at each feature's entry, in order. */
  headings: number[];
  /** World exit point after the last feature (where a landing pad goes). */
  exit: Vector3;
  /** Final heading, degrees. */
  headingDeg: number;
}

/**
 * Walks a cursor through an explicit feature list, chaining each feature's true
 * exit (via `featureExit`/`buildLane`) into the next one's entry — the same
 * connection math `randomSpec` uses, but in a hand-chosen, deterministic order.
 * Returns the placements plus each feature's entry point (for zone/landmark
 * placement) and the final exit. This is how a bespoke, guaranteed-surfable
 * course (e.g. the Arcane Castle) is authored without hand-solving transforms.
 */
export function buildCourseSequence(
  start: Vector3,
  headingDeg: number,
  features: CourseFeature[],
): CourseSequence {
  let heading = headingDeg;
  const cursor = start.clone();
  const placements: Placement[] = [];
  const entries: Vector3[] = [];
  const headings: number[] = [];
  for (const f of features) {
    const { local, turnDeg, pitch } = featureExit(f.shapeId, f.params);
    const usePitch = f.pitchDeg !== undefined ? toRad(f.pitchDeg) : pitch;
    entries.push(cursor.clone());
    headings.push(heading);
    placements.push({
      shapeId: f.shapeId,
      params: f.params,
      position: arr(cursor),
      rotation: [usePitch, toRad(heading), 0],
    });
    cursor.copy(
      local
        .clone()
        .applyEuler(new Euler(usePitch, toRad(heading), 0, 'YXZ'))
        .add(cursor),
    );
    heading += turnDeg;
  }
  return { placements, entries, headings, exit: cursor.clone(), headingDeg: heading };
}

function effectiveRole(p: Placement): ShapeRole {
  return p.role ?? SHAPES[p.shapeId].role;
}

function materialForRole(role: ShapeRole, m: MaterialSet) {
  return role === 'platform'
    ? m.platform
    : role === 'ground'
      ? m.ground
      : role === 'detail'
        ? m.decor
        : m.ramp;
}

/**
 * Builds the Three.js mesh for a placement. The mesh carries a back-reference to
 * its placement in `userData.placement`, so a gizmo edit can sync the transform
 * back via `syncPlacement`.
 */
export function meshForPlacement(p: Placement, materials: MaterialSet): Mesh {
  const def = SHAPES[p.shapeId];
  const geometry = def.geometry({ ...def.defaults, ...p.params });
  const role = effectiveRole(p);
  generateBoxUVs(geometry, role === 'ground' ? 512 : 256);
  const mesh = new Mesh(geometry, materialForRole(role, materials));
  mesh.position.fromArray(p.position);
  mesh.rotation.order = 'YXZ';
  mesh.rotation.set(p.rotation[0], p.rotation[1], p.rotation[2]);
  if (p.scale) {
    mesh.scale.fromArray(p.scale);
  }
  mesh.castShadow = role !== 'detail';
  mesh.receiveShadow = true;
  mesh.userData.placement = p;
  return mesh;
}

/** Copies a (gizmo-edited) object's transform back into its placement. */
export function syncPlacement(p: Placement, object: Object3D): void {
  p.position = [object.position.x, object.position.y, object.position.z];
  p.rotation = [object.rotation.x, object.rotation.y, object.rotation.z];
  p.scale = [object.scale.x, object.scale.y, object.scale.z];
}

/**
 * Instantiates a spec into a scene as a `BuiltLevel` — the same shape the game's
 * loaders return, so the editor's Play mode and any future runtime path can hand
 * it straight to LevelCollider + PlayerController. Non-detail placements collide.
 */
export function buildLevelFromPlacements(
  spec: PlacementMapSpec,
  parent: Scene | Group,
  materials: MaterialSet,
): BuiltLevel {
  const colliders: Mesh[] = [];
  for (const p of spec.placements) {
    const mesh = meshForPlacement(p, materials);
    parent.add(mesh);
    if (effectiveRole(p) !== 'detail') {
      colliders.push(mesh);
    }
  }
  return {
    mapId: spec.id,
    colliders,
    spawn: new Vector3().fromArray(spec.spawn),
    spawnYaw: spec.spawnYaw,
    zones: [],
    killY: spec.killY,
  };
}

/** Convenience for non-editor callers (tests, future runtime). */
export async function loadPlacementLevel(
  spec: PlacementMapSpec,
  scene: Scene,
): Promise<BuiltLevel> {
  return buildLevelFromPlacements(spec, scene, await loadMaterials());
}

export function serializeSpec(spec: PlacementMapSpec): string {
  return JSON.stringify(spec, null, 2);
}

/** Parses + validates a saved spec. Throws on anything malformed. */
export function parseSpec(json: string): PlacementMapSpec {
  const raw = JSON.parse(json) as Partial<PlacementMapSpec>;
  if (raw.version !== 1 || !Array.isArray(raw.placements)) {
    throw new Error('Not a Fall Line map (expected version 1 with a placements array).');
  }
  for (const p of raw.placements) {
    if (!(p.shapeId in SHAPES) || !Array.isArray(p.position) || !Array.isArray(p.rotation)) {
      throw new Error(`Invalid placement: ${JSON.stringify(p)}`);
    }
  }
  return {
    version: 1,
    id: raw.id ?? 'untitled',
    name: raw.name ?? 'Untitled',
    spawn: raw.spawn ?? [0, 200, 0],
    spawnYaw: raw.spawnYaw ?? 0,
    killY: raw.killY,
    placements: raw.placements,
  };
}
